import { useState } from "react";
import { Loader2, FileEdit, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import VisibilityPicker from "./VisibilityPicker";

const PLACEHOLDER = `Scene 5 — watch out for cup placement
Kelly should hold the cup in her left hand

6 — Goring tie loosens between scenes 5 and 6

Stray note with no scene reference
`;

/**
 * Iter-60: Owner-only Supervisor Notes import dialog.
 * Pasted text is parsed line-by-line, auto-mapped to scene_number +
 * character (when names match the project roster), and stored in the
 * dedicated `supervisor_notes` collection — NEVER merged into per-pair
 * department continuity notes.
 */
export default function SupervisorNotesImportDialog({
    projectId,
    open,
    onOpenChange,
    onImported,
}) {
    const apiBase = useApiBase();
    const [content, setContent] = useState("");
    const [label, setLabel] = useState("");
    // Iter-156E — visibility selector parity. Applied to every note
    // in the batch on the backend. Defaults to "everyone".
    const [visibility, setVisibility] = useState("everyone");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const reset = () => {
        setContent("");
        setLabel("");
        setVisibility("everyone");
        setResult(null);
        setError("");
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!content.trim()) {
            setError("Paste at least one supervisor note.");
            return;
        }
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/supervisor-notes/import`,
                {
                    content,
                    label: label.trim() || null,
                    visibility,
                },
            );
            setResult(data);
            toast.success(`Imported ${data.imported} notes`);
            if (onImported) onImported(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!submitting) {
                    onOpenChange(v);
                    if (!v) reset();
                }
            }}
        >
            <DialogContent
                data-testid="supervisor-notes-import-dialog"
                className="max-w-xl bg-card border-white/10 max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <FileEdit size={16} className="text-primary" />
                        Import notes
                    </DialogTitle>
                    <DialogDescription className="text-neutral-400">
                        Paste typed or transcribed notes. Lines starting with{" "}
                        <span className="text-white font-mono text-xs">
                            Scene N
                        </span>{" "}
                        anchor a block; character names in each line are auto-mapped.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <div className="space-y-3 mt-2">
                        <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-4 flex items-start gap-3">
                            <CheckCircle2
                                size={18}
                                className="text-emerald-700 dark:text-emerald-200 mt-0.5 shrink-0"
                            />
                            <div>
                                <p
                                    data-testid="supervisor-notes-import-summary"
                                    className="text-emerald-900 dark:text-emerald-50 font-semibold"
                                >
                                    {result.imported} note
                                    {result.imported === 1 ? "" : "s"} imported
                                </p>
                                <p className="text-[13px] text-emerald-900 dark:text-emerald-50 mt-1">
                                    {result.mapped_scene} scenes matched ·{" "}
                                    {result.mapped_character} characters matched
                                    {typeof result.unrecognised === "number"
                                        ? ` · ${result.unrecognised} not recognised`
                                        : ""}
                                </p>
                                {result.imported === 0 ||
                                (result.mapped_scene === 0 &&
                                    result.mapped_character === 0) ? (
                                    <p
                                        data-testid="supervisor-notes-import-no-match"
                                        className="text-[11px] text-amber-300/90 mt-1.5"
                                    >
                                        No scene or character references
                                        detected. Use lines like{" "}
                                        <span className="font-mono">
                                            Scene 5
                                        </span>{" "}
                                        or include a character name.
                                    </p>
                                ) : null}
                            </div>
                        </div>
                        {result.looks_like_script ? (
                            <div
                                data-testid="supervisor-notes-import-script-hint"
                                className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-100 flex items-start gap-2"
                            >
                                <AlertTriangle
                                    size={13}
                                    className="text-amber-300 mt-0.5 shrink-0"
                                />
                                <span>
                                    This looks like a script. Please paste reference notes only.
                                </span>
                            </div>
                        ) : null}
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                data-testid="supervisor-notes-import-another"
                                onClick={reset}
                                className="flex-1 min-tap"
                            >
                                Import another batch
                            </Button>
                            <Button
                                type="button"
                                data-testid="supervisor-notes-import-close"
                                onClick={() => {
                                    onOpenChange(false);
                                    reset();
                                }}
                                className="flex-1 min-tap font-semibold"
                            >
                                Done
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3 mt-2">
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="sup-notes-label"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Batch label (optional)
                            </Label>
                            <Input
                                id="sup-notes-label"
                                data-testid="supervisor-notes-label-input"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="Day 12 supervisor notes"
                                maxLength={120}
                                className="h-11 bg-secondary border-transparent text-base"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label
                                htmlFor="sup-notes-content"
                                className="text-[10px] uppercase tracking-[0.2em] text-neutral-500"
                            >
                                Notes
                            </Label>
                            <Textarea
                                id="sup-notes-content"
                                data-testid="supervisor-notes-content-input"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder={PLACEHOLDER}
                                rows={10}
                                className="bg-secondary border-transparent resize-y text-sm leading-relaxed"
                            />
                        </div>
                        {/* Iter-156E — visibility selector parity. Applied
                            to every note in the batch. */}
                        <VisibilityPicker
                            value={visibility}
                            onChange={setVisibility}
                            testidPrefix="supervisor-notes-import-visibility"
                        />
                        {error ? (
                            <div
                                data-testid="supervisor-notes-import-error"
                                className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-200"
                            >
                                <AlertTriangle
                                    size={13}
                                    className="text-red-300 mt-0.5 shrink-0"
                                />
                                {error}
                            </div>
                        ) : null}
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                data-testid="supervisor-notes-import-cancel"
                                variant="secondary"
                                onClick={() => {
                                    onOpenChange(false);
                                    reset();
                                }}
                                disabled={submitting}
                                className="flex-1 min-tap"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                data-testid="supervisor-notes-import-submit"
                                disabled={submitting || !content.trim()}
                                className="flex-1 min-tap font-semibold"
                            >
                                {submitting ? (
                                    <Loader2
                                        size={14}
                                        className="animate-spin"
                                    />
                                ) : (
                                    "Import Notes"
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
